import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  FC,
} from 'react';
import { config } from '../config';
import {
  TWindowType,
  TAgentNav,
  ICannedReply,
  IUserInfo,
  IContext,
} from '../interfaces';
import { popupWindow } from '../helpers/popupWindow';
import {
  checkCannedResponses,
  getCannedResponses,
  getCustomerData,
  getUserInfo,
} from '../api';
import { filterCannedResponses } from '../helpers/filterCannedResponses';
import useSound from 'use-sound';

const { url, connectUrl, region, instanceId } = config;

const initialValues: IContext = {
  loaded: false,
  activeTab: 'softphone',
  setActiveTab: (): void => {
    throw new Error('setContext function must be overridden');
  },
  contact: undefined,
  agentNav: null,
  setAgentNav: (): void => {
    throw new Error('setContext function must be overridden');
  },
  cannedResponses: [],
  cannedResponsesPopup: [],
  shortcut: '',
  shortcuts: [],
  setShortcut: (): void => {
    throw new Error('setContext function must be overridden');
  },
  agentInfo: null,
  setCannedResponses: (): void => {
    throw new Error('setContext function must be overridden');
  },
  setShortcuts: (): void => {
    throw new Error('setContext function must be overridden');
  },
  agentChatSession: undefined,
  setContact: (): void => {
    throw new Error('setContact function must be overridden');
  },
};

export const Connect = createContext<IContext>(initialValues);

export function useConnect() {
  return useContext(Connect);
}

export const ConnectProvider: FC = ({ children }) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [agentInfo, setAgentInfo] = useState<IUserInfo | null>(null);
  const [activeTab, setActiveTab] = useState<TWindowType>('softphone');
  const [agentNav, setAgentNav] = useState<TAgentNav | null>(null);
  const [contact, setContact] = useState<connect.Contact | undefined>(
    undefined
  );
  const [agentChatSession, setAgentChatSession] = useState<
    connect.AgentChatSession | undefined
  >(undefined);
  const [shortcut, setShortcut] = useState<string>('');

  const loginWin = useRef<any>(null);
  const [playNotification] = useSound('/audio/notice.mp3');
  const cannedResponsesJSON = window.localStorage.getItem('canned_responses');

  const [cannedResponses, setCannedResponses] = useState<ICannedReply[]>(
    cannedResponsesJSON ? JSON.parse(cannedResponsesJSON).canned_replies : []
  );
  const [shortcuts, setShortcuts] = useState<string[]>(
    cannedResponsesJSON ? JSON.parse(cannedResponsesJSON).shortcuts : []
  );

  const getConnection = async (contact: connect.BaseConnection | undefined) => {
    //@ts-ignore
    const session = await contact.getMediaController();
    setAgentChatSession(session);
  };

  useEffect(() => {
    //@ts-ignore (use ts-ignore because this method is missing from the declaration)
    Object.values(agentChatSession?.controller?.pubsub.subMap.subIdMap || {})
      //@ts-ignore (use ts-ignore because this method is missing from the declaration)
      ?.find((sub) => sub.eventName === 'INCOMING_MESSAGE')?.unsubscribe();

    agentChatSession?.onMessage((event) => {
      const { ParticipantRole, Type, ContactId } = event.data;
      if (ParticipantRole === 'CUSTOMER' && Type === 'MESSAGE') {
        if (
          localStorage.getItem('lastContactId') !== ContactId ||
          activeTab !== 'chat' ||
          document.hidden
        ) {
          playNotification();
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentChatSession, playNotification, document.hidden, activeTab]);

  useEffect(() => {
    let popup = false;
    let loading = false;

    // init app
    window.connect.core.initCCP(document.getElementById('stream')!, {
      ccpUrl: `${connectUrl}/ccp-v2/`,
      loginPopup: false,
      region,
      softphone: {
        allowFramedSoftphone: true,
        disableRingtone: false,
        ringtoneUrl: `${url}/ringtone.mp3`,
      },
    });

    // callback after app initialization to determine the current contact if any
    //@ts-ignore (use ts-ignore because this method is missing from the declaration)
    window.connect.core.onInitialized(() => {
      window.connect.agent((_agent) => {
        const configuration = _agent.getConfiguration();
        const currentContacts = _agent.getContacts();

        const isChatContact = currentContacts.some(
          (i) => i.getType() === 'chat'
        );
        if (isChatContact) {
          const lastContactId = window.localStorage.getItem('lastContactId');

          window.connect.core.viewContact(
            lastContactId
              ? currentContacts.find((i) => i.contactId === lastContactId)
                  ?.contactId || currentContacts[0].contactId
              : currentContacts[0].contactId
          );
          setAgentNav('customer_info');
        }
        const username = configuration.username;
        const cannedResponses = window.localStorage.getItem('canned_responses');
        if (!cannedResponses) {
          getCannedResponses(username, { setCannedResponses, setShortcuts });
        } else {
          checkCannedResponses(username, { setCannedResponses, setShortcuts });
        }
        getUserInfo(instanceId, username, setAgentInfo);
      });

      setLoaded(true);
    });

    // set the active tab to the state after a program change
    //@ts-ignore (use ts-ignore because this method is missing from the declaration)
    window.connect.core.onActivateChannelWithViewType((_view: any) => {
      setActiveTab(_view.mediaType);

      window.connect.agent((_agent) => {
        const currentContacts = _agent.getContacts();

        if (_view.mediaType === 'softphone') {
          const phoneContact = currentContacts.find(
            (i) => i.getType() === 'voice'
          );
          window.connect.core.viewContact(phoneContact?.contactId || '');
        }

        if (_view.mediaType === 'chat') {
          if (currentContacts.some((i) => i.getType() === 'chat')) {
            const lastContactId = window.localStorage.getItem('lastContactId');

            window.connect.core.viewContact(
              lastContactId
                ? currentContacts.find((i) => i.contactId === lastContactId)
                    ?.contactId || currentContacts[0].contactId
                : currentContacts[0].contactId
            );
          }
        }
      });
    });

    window.connect.contact((_contact) => {
      _contact.onEnded(() => {
        if (_contact.getType() === 'chat') {
          setAgentChatSession(undefined);
        }
      });

      _contact.onDestroy(() => {
        setContact((prev) => {
          if (prev?.contactId === _contact.contactId) return undefined;
          return prev;
        });
      });

      _contact.onConnected(() => {
        if (!('IterableData' in _contact.getAttributes())) {
          getCustomerData({ contact: _contact, instanceId }, { setContact });
        }
      });

      _contact.onMissed(() => {
        if (_contact.getType() === 'voice') {
          setContact((prev) => {
            if (prev?.contactId === _contact.contactId) return undefined;
            return prev;
          });
        }
        setTimeout(
          () =>
            _contact.clear({
              success: () => console.log('sucess'),
              failure: () => console.log('failure'),
            }),
          3000
        );
      });
    });

    window.connect.core.onViewContact((_view) => {
      if (_view.contactId) {
        window.connect.agent((_agent) => {
          const currentContact = _agent
            .getContacts()
            .find((i) => i.contactId === _view.contactId);

          if (currentContact) {
            const contactType =
              currentContact.getType() === 'chat' ? 'chat' : 'softphone';
            if (contactType === 'chat') {
              window.localStorage.setItem('lastContactId', _view.contactId);
              const cnn = currentContact
                .getConnections()
                .find(
                  (i) => i.getType() === window.connect.ConnectionType.AGENT
                );
              getConnection(cnn);
            }
            setAgentNav('customer_info');
            setActiveTab(contactType);
            setContact(currentContact);
          }
        });
      } else {
        setActiveTab('softphone');
        setContact(undefined);
      }
    });

    function closeLoginPopup() {
      if (loginWin.current) {
        loginWin.current.close();
        loginWin.current = undefined;
      }
    }

    function spawnLoginPopup() {
      loginWin.current = popupWindow(connectUrl + '/login', 450, 570);
    }

    //@ts-ignore
    const eventBus = window.connect.core.getEventBus();
    //@ts-ignore
    eventBus.subscribe(window.connect.EventType.ACK_TIMEOUT, () => {
      if (!popup) {
        loading = true;
        popup = true;
        spawnLoginPopup();
      }
    });
    //@ts-ignore
    eventBus.subscribe(window.connect.EventType.ACKNOWLEDGE, () => {
      if (loading) {
        loading = false;
        popup = false;
        setLoaded(true);
        closeLoginPopup();
      }
    });
    //@ts-ignore
    eventBus.subscribe(window.connect.EventType.TERMINATED, () => {
      setLoaded(false);
    });
  }, []);

  const value: IContext = {
    loaded,
    activeTab,
    setActiveTab,
    contact,
    agentNav,
    setAgentNav,
    cannedResponses: filterCannedResponses(cannedResponses, shortcut),
    cannedResponsesPopup: cannedResponses,
    shortcut,
    shortcuts,
    setShortcut,
    agentInfo,
    setCannedResponses,
    setShortcuts,
    agentChatSession,
    setContact,
  };

  return <Connect.Provider value={value}>{children}</Connect.Provider>;
};
